import Big from 'big.js';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect } from 'react';
import { AiFillInfoCircle } from 'react-icons/ai';
import Switch from 'react-switch';

import DeliveryMode from '../../store/deliveryMode';
import GuestUserStore from '../../store/guestUser';
import { Order } from '../../store/order';
import User from '../../store/user';
import OrderCouponForm from '../OrderFormComponents/OrderCouponForm';
import OrderInvoiceForm from '../OrderFormComponents/OrderInvoiceForm';

import styles, { boxShadow, brandColor, brandColorMuted } from './OrderPreferences.module.scss';
import UserPaymentOptions from './UserPaymentOptions';

const phoneCommunication = {
  value: 'phone',
  label: 'Τηλεφωνική επικοινωνία, θα σε καλέσουμε να επιλέξουμε μαζί το προϊόν αντικατάστασης',
};
const smartReplace = {
  value: 'replace',
  label: 'Έξυπνη αντικατάσταση, θα αντικαταστήσουμε το προϊόν με ένα αντίστοιχης ποιότητας και αξίας',
};
const noChange = {
  value: 'none',
  label: 'Καμία αλλαγή, θα αφαιρέσουμε το προϊόν από την παραγγελία σου και το τελικό κόστος',
};

function OrderPreferences({ bagsInfo = {}, control, couponNo }) {
  const [bagsUse, setBagsUse] = useState(true);
  const [pointsUse, setPointsUse] = useState(false);
  const [bagsDisabled, setBagsDisabled] = useState(false);
  const [pointsDisabled, setPointsDisabled] = useState(false);
  const [service, setService] = useState(undefined);
  const [wordCount, setWordCount] = useState(0);
  const [changesOption, setChangesOption] = useState('phone');
  const [guestUser, setGuestUser] = useState({});

  function onChangeChangesAllowed(option) {
    setChangesOption(option.target.value);
    Order.setChangesAllowed(option.target.value);
  }

  function onChangePointsUse() {
    setPointsUse(!pointsUse);
    Order.setPointsUse(!pointsUse);
  }

  function onChangePlasticBags() {
    Order.setPlasticBags(!bagsUse);
    setBagsUse(!bagsUse); // TODO: use a single source of truth
  }

  function onChangeComment(e) {
    Order.setComments(e.target.value);
    setWordCount(e.target.value.length);
  }

  useEffect(() => {
    Order.setChangesAllowed('phone');
    Order.setPlasticBags(true);
    Order.setComments(undefined);

    const deliveryMode$ = DeliveryMode.subscribe(mode => {
      setService(mode);
      if (mode === 'express') {
        setBagsDisabled(true);
      }
    });

    const user$ = User.subscribe(user => {
      if (isEmpty(user)) {
        return;
      }
      if (user.points < user.redeemPoints) {
        setPointsDisabled(true);
      }
    });

    const guestUser$ = GuestUserStore.subscribe(_guestUser => {
      setGuestUser(_guestUser);
    });

    return () => {
      deliveryMode$.unsubscribe();
      user$.unsubscribe();
      guestUser$.unsubscribe();
    };
  }, []);

  const bagPrice = bagsInfo.price
    ? Big(bagsInfo.price)
      .div(bagsInfo.amount || 1)
      .div(100)
    : process.env.plasticBagFee;
  const bagPriceText = `${bagPrice.toFixed(2)}  €`;

  return (
    <>
      <UserPaymentOptions />
      <section className={styles.section}>
        <h2 className={styles.preferencesTitle}>ΠΡΟΤΙΜΗΣΕΙΣ ΠΑΡΑΓΓΕΛΙΑΣ</h2>
        <p className={styles.instructions}>Συμπλήρωσε τις προτιμήσεις σου σχετικά με την παραγγελία σου</p>
        <div className={styles.checkBoxContainer}>
          <p className={styles.labelText}>Όταν δεν υπάρχει διαθέσιμο κάποιο προϊόν επιθυμείς:</p>
          <div className={styles.radioContainer}>
            <input
              type="radio"
              name="radioCommunication"
              id="radioCommunication"
              checked={changesOption === phoneCommunication.value}
              value={phoneCommunication.value}
              onChange={onChangeChangesAllowed}
              className={styles.radioOption}
            />
            <label htmlFor="radioCommunication" className={styles.radioLabel}>
              {phoneCommunication.label}
            </label>
          </div>
          <div className={styles.radioContainer}>
            <input
              type="radio"
              name="radioReplace"
              id="radioReplace"
              checked={changesOption === smartReplace.value}
              value={smartReplace.value}
              onChange={onChangeChangesAllowed}
              className={styles.radioOption}
            />
            <label htmlFor="radioReplace" className={styles.radioLabel}>
              {smartReplace.label}
            </label>
          </div>
          <div className={styles.radioContainer}>
            <input
              type="radio"
              name="noChange"
              id="noChange"
              checked={changesOption === noChange.value}
              value={noChange.value}
              onChange={onChangeChangesAllowed}
              className={styles.radioOption}
            />
            <label htmlFor="noChange" className={styles.radioLabel}>
              {noChange.label}
            </label>
          </div>
        </div>
        <div className={styles.checkBoxContainer}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.checkBoxLabel}>
            <span className={cn(styles.labelText, { [styles.opacity]: guestUser?.email })}>
              Εξαργύρωση πόντων{' '}
              {guestUser?.email && (
                <span title="Κάνε εγγραφή και εξαργύρωσε τους πόντους σου">
                  <AiFillInfoCircle className={styles.infoIcon} />
                </span>
              )}
            </span>

            <Switch
              disabled={pointsDisabled || Boolean(guestUser?.email)}
              checked={pointsUse}
              onChange={onChangePointsUse}
              onColor={brandColorMuted}
              onHandleColor={brandColor}
              handleDiameter={20}
              height={14}
              width={32}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow={boxShadow}
              activeBoxShadow={boxShadow}
              className="react-switch"
            />
          </label>
          <p className={cn(styles.details, { [styles.opacity]: guestUser?.email })}>
            Σας ενημερώνουμε ότι το κατώτερο όριο εξαργύρωσης πόντων είναι 500 πόντοι
          </p>
        </div>
        <OrderCouponForm control={control} couponNo={couponNo} guestUser={guestUser} />
        {process.env.hasInvoice && <OrderInvoiceForm control={control} />}

        <div className={styles.checkBoxContainer}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="" className={styles.checkBoxLabel} data-cy="plastic-bags-switch">
            <span className={styles.labelText}>Επιθυμώ σακούλες με χρέωση</span>
            <Switch
              disabled={bagsDisabled}
              checked={bagsUse}
              onChange={onChangePlasticBags}
              onColor={brandColorMuted}
              onHandleColor={brandColor}
              handleDiameter={20}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow={boxShadow}
              activeBoxShadow={boxShadow}
              className="react-switch"
              height={14}
              width={32}
            />
          </label>
          <div className={styles.details}>
            {service === 'express' && (
              <p>Η χρήση σακούλας είναι απαραίτητη για την παράδοση της Express παραγγελίας σου.</p>
            )}
            {bagsUse ? (
              <p>
                {bagsInfo.amount}{' '}
                {`Bιοδιασπώμεν${bagsInfo.amount > 1 ? 'ες' : 'η'} σακούλ${bagsInfo.amount > 1 ? 'ες' : 'α'}`}{' '}
                (περιβαλλοντικό τέλος {bagPriceText}
                /σακούλα)
              </p>
            ) : (
              <p>Bιοδιασπώμενες σακούλες {bagPriceText}/τμχ, κατασκευασμένες από υλικό 100% φυτικής προέλευσης.</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="comments" className={styles.textAreaLabel}>
            <span className={styles.labelText}>Σχόλια Παραγγελίας</span>
            <textarea name="comments" id="comments" className={styles.textArea} onChange={onChangeComment} />
            <span className={styles.wordCountText}>{`${wordCount} / 500`}</span>
          </label>
        </div>
      </section>
    </>
  );
}

export default OrderPreferences;
